import { ViewStateActions, ViewStateApiActions } from '@common/store/navigation/actions/viewstate.actions';
import { ViewStateState } from '@common/store/navigation/models/viewstate.models';
import { createReducer, on } from '@ngrx/store';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { PUBLICATION_LIST_TIME_DAYS_MAP } from '@src/suite/common/enums/constants';
import { ViewStateFilterElevations, ViewStateFilterStates } from '@src/suite/common/enums/viewstate-filter-states';
import { ViewStateLayouts } from '@src/suite/common/enums/viewstate-layouts';
import { ViewStateSortOders } from '@src/suite/common/enums/viewstate-sort-orders';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import {
    isFilteredViewStateRecord,
    isPublicationViewStateRecord,
    isSingleViewStateRecord,
    isViewStateSupportsGroupBy,
    isViewStateSupportsLayout,
    isViewStateSupportsSortBy,
} from '@src/suite/common/utils/viewstate-utils';
import { DateTime } from 'luxon';

import { ApplicationActions } from '../actions/application.actions';
import { NavigationApiActions } from '../actions/navigation.actions';

export const initialState: ViewStateState = {
    applicationName: null,
    viewState: null,
    planningViewsFilterState: ViewStateFilterStates.Collapsed,
    filterElevation: 'side',
    mainCta: null,
    secondaryCta: null,
    viewOptions: null,
    rightSlot: null,
    viewStates: [],
    viewStatesLoading: false,
};

function handleUpdateViewState(state: ViewStateState, vs: StorytellingViewStateRecord): ViewStateState {
    if (state.viewState?.Guid !== vs.Guid) {
        return state;
    }
    const newViewState = { ...vs };
    if (newViewState.PSTViewStateType === ViewStateTypes.PUBLICATION) {
        (newViewState as StorytellingPublicationViewStateRecord).maxPublicationRevisionNumber = (vs as StorytellingPublicationViewStateRecord).maxPublicationRevisionNumber;
        (newViewState as StorytellingPublicationViewStateRecord).publicationRevisionNumber = (vs as StorytellingPublicationViewStateRecord).publicationRevisionNumber;
    }
    return {
        ...state,
        viewState: newViewState,
    };
}

export const viewStateReducer = createReducer(
    initialState,
    on(ApplicationActions.sessionDestroyed, (): ViewStateState => ({ ...initialState })),
    on(ViewStateActions.setCurrentApplication, (state, action): ViewStateState => {
        return {
            ...state,
            applicationName: action.application,
        };
    }),
    on(ViewStateActions.setCurrentViewStateSuccess, (state, action): ViewStateState => {
        if (action.vs) {
            const vs: StorytellingViewStateRecord = { ...action.vs };

            // Normalize PSTViewStateFilterState to boolean (server might return 0/1 instead of false/true)
            if (vs.PSTViewStateFilterState != null) {
                vs.PSTViewStateFilterState = !!vs.PSTViewStateFilterState;
            }

            if (isViewStateSupportsLayout(vs) && action.layout) {
                const layout = { ...action.layout };
                layout.Value = vs.PSTViewStateLayout?.Value || layout.Default;
                vs.PSTViewStateLayout = layout;
            }

            if (isViewStateSupportsSortBy(vs) && action.sortBy) {
                const sortBy = { ...action.sortBy };
                sortBy.Value = (vs as StorytellingSingleViewStateRecord).PSTViewStateSorting?.Value || sortBy.Default;
                (vs as StorytellingSingleViewStateRecord).PSTViewStateSorting = sortBy;
            }

            if (isViewStateSupportsGroupBy(vs) && action.groupBy) {
                const groupBy = { ...action.groupBy };
                groupBy.Value = (vs as StorytellingSingleViewStateRecord).PSTViewStateGrouping?.Value || groupBy.Default;
                (vs as StorytellingSingleViewStateRecord).PSTViewStateGrouping = groupBy;
            }

            if (isSingleViewStateRecord(vs) || vs.PSTViewStateType === ViewStateTypes.PUBLICATION) {
                if (
                    vs.PSTViewStateType === ViewStateTypes.TOPIC ||
                    vs.PSTViewStateType === ViewStateTypes.SEARCH ||
                    vs.PSTViewStateType === ViewStateTypes.PUBLICATION ||
                    vs.PSTViewStateType === ViewStateTypes.WASTEBINSEARCH
                ) {
                    if (vs.PSTViewStateType === ViewStateTypes.PUBLICATION) {
                        if (vs.PSTViewStateLayout.Value === ViewStateLayouts.CARD) {
                            vs.PSTViewStateLayout.Default = ViewStateLayouts.CONDENSE;
                            vs.PSTViewStateLayout.Value = ViewStateLayouts.CONDENSE;
                        }
                        // add publication time
                        const publicationTimeFilter = (vs as StorytellingPublicationViewStateRecord)?.PSTViewStateFilter?.Filters?.find(f => f.ID === PSTFieldNames.PSTPublicationTime);
                        if (publicationTimeFilter?.Value && PUBLICATION_LIST_TIME_DAYS_MAP[publicationTimeFilter.Value] != null) {
                            let dt = DateTime.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
                            dt = dt.plus({ days: PUBLICATION_LIST_TIME_DAYS_MAP[publicationTimeFilter.Value] });
                            (vs as StorytellingPublicationViewStateRecord).publicationTime = dt;
                        }
                    }
                    // Set default custom card fields
                    if (!(vs as StorytellingSingleViewStateRecord).PSTViewStateTopicCardCustomItems || !Array.isArray((vs as StorytellingSingleViewStateRecord).PSTViewStateTopicCardCustomItems)) {
                        (vs as StorytellingSingleViewStateRecord).isViewStateCustomCardItemsBlank = true;
                    } else {
                        (vs as StorytellingSingleViewStateRecord).isViewStateCustomCardItemsBlank = false;
                    }
                }
            }

            if (isSingleViewStateRecord(vs) || isPublicationViewStateRecord(vs)) {
                (vs as StorytellingSingleViewStateRecord).isViewStateCustomCardItemsBlank = (vs as StorytellingSingleViewStateRecord).isViewStateCustomCardItemsBlank;
            }

            if (isFilteredViewStateRecord(vs) && action?.vsFilter) {
                const filter = { ...action?.vsFilter };
                (vs as StorytellingSingleViewStateRecord).PSTViewStateFilter = (vs as StorytellingSingleViewStateRecord)?.PSTViewStateFilter ?? filter;
            }

            return {
                ...state,
                viewState: vs,
            };
        } else {
            return {
                ...state,
                viewState: null,
            };
        }
    }),
    on(ViewStateActions.addViewStateFilters, (state, action): ViewStateState => {
        if (isFilteredViewStateRecord(state.viewState)) {
            const vs = { ...(state.viewState as StorytellingSingleViewStateRecord) };
            const vsFilter = { ...(state.viewState as StorytellingSingleViewStateRecord).PSTViewStateFilter };
            vsFilter.Filters = vsFilter.Filters.map(filter => {
                if (action.filters.find(f => f === filter.ID)) {
                    if (action.required) filter = { ...filter, IsRequired: true, IsOptional: false, NotUsed: false };
                    else filter = { ...filter, IsRequired: false, IsOptional: true, NotUsed: false };
                } else {
                    if (action.required) filter = { ...filter, IsRequired: false };
                    else filter = { ...filter, IsOptional: false };
                }
                return filter;
            });
            vs.PSTViewStateFilter = vsFilter;

            return {
                ...state,
                viewState: vs,
            };
        }
    }),
    on(ViewStateActions.removeViewStateFilter, (state, action): ViewStateState => {
        if (isFilteredViewStateRecord(state.viewState)) {
            const vs = { ...(state.viewState as StorytellingSingleViewStateRecord) };
            const vsFilter = { ...(state.viewState as StorytellingSingleViewStateRecord).PSTViewStateFilter };
            vsFilter.Filters = vsFilter.Filters.map(filter => {
                if (action.filter === filter.ID) {
                    filter = { ...filter, IsRequired: false, IsOptional: false, NotUsed: true };
                }
                return filter;
            });
            vs.PSTViewStateFilter = vsFilter;

            return {
                ...state,
                viewState: vs,
            };
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateFilter, ViewStateActions.adjustViewStateFilter, (state, action): ViewStateState => {
        if (state.viewState) {
            const vs = { ...state.viewState } as StorytellingSingleViewStateRecord;
            vs.PSTViewStateFilter = {
                ...vs.PSTViewStateFilter,
                Filters: vs.PSTViewStateFilter.Filters.map(f => {
                    if (f.ID === action.filter?.ID) {
                        f = { ...action.filter };
                    }
                    return f;
                }),
            };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.adjustDateRange, (state, action): ViewStateState => {
        if (isSingleViewStateRecord(state.viewState)) {
            const vs = { ...state.viewState } as StorytellingSingleViewStateRecord;
            vs.PSTViewStateFilter = {
                ...vs.PSTViewStateFilter,
                Filters: vs.PSTViewStateFilter.Filters.map(f => {
                    if (f.ID === PSTFieldNames.TargetPublicationDate) {
                        f = {
                            ...f,
                            Value: {
                                from: action.firstDate,
                                to: action.lastDate,
                                custom: null,
                            },
                        };
                    }
                    return f;
                }),
            };
            return handleUpdateViewState(state, vs);
        } else if (state.viewState.PSTViewStateType === ViewStateTypes.PUBLICATION) {
            // const vs = state.viewState as PublicationViewStateRecord;
            // const filter = vs.viewStateFilter.deepCopy();
            // let targetDateFilter: FilterDef = filter.filters.find(f => f.ID === PSTFieldNames.PSTPublicationTime);
            // if (!targetDateFilter) {
            //     targetDateFilter = new FilterDef();
            //     targetDateFilter.ID = PSTFieldNames.PSTPublicationTime;
            //     targetDateFilter.PSTField = PSTFieldNames.PSTPublicationTime;
            //     targetDateFilter.Validation = FieldDefValidations.PSTPUBLICATIONLISTTIME;
            //     filter.filters.push(targetDateFilter);
            // }
            // if (targetDateFilter) {
            //     const dateRange = new DateRange();
            //     dateRange.from = action.firstDate;
            //     dateRange.to = action.lastDate;
            //     dateRange.custom = null;
            //     targetDateFilter.Value = dateRange;
            //     vs.viewStateFilter = filter;
            //     return handleUpdateViewState(state, vs);
            // }

            const vs = { ...state.viewState } as StorytellingPublicationViewStateRecord;
            let time = vs.publicationTime;
            if (!DateTime.isDateTime(time) || !time.isValid) {
                const dt = DateTime.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
                (vs as StorytellingPublicationViewStateRecord).publicationTime = dt;
                time = dt;
            }

            if (action.firstDate) {
                time = action.firstDate;
            }
            vs.publicationTime = time;
            return handleUpdateViewState(state, vs);
        }

        return state;
    }),
    on(ViewStateActions.updateViewStateFilterState, (state, action): ViewStateState => {
        if (state.viewState) {
            const vs = { ...state.viewState } as StorytellingViewStateRecord;
            vs.PSTViewStateFilterState = action.opened;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateLayout, (state, action): ViewStateState => {
        if (state.viewState) {
            const vs = { ...state.viewState } as StorytellingSingleViewStateRecord;
            vs.PSTViewStateLayout = { ...vs.PSTViewStateLayout, Value: action.layout };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateGroupBy, (state, action): ViewStateState => {
        if (state.viewState) {
            const vs = { ...state.viewState } as StorytellingSingleViewStateRecord;
            vs.PSTViewStateGrouping = { ...vs.PSTViewStateGrouping, Value: action.groupBy };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateSortBy, (state, action): ViewStateState => {
        if (state.viewState) {
            const vs = { ...state.viewState } as StorytellingSingleViewStateRecord;
            vs.PSTViewStateSorting = { ...vs.PSTViewStateSorting, Value: action.sortBy };
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.toggleViewStateZoomToCoverage, (state): ViewStateState => {
        if (state.viewState && state.viewState.PSTViewStateType === ViewStateTypes.TOPIC) {
            const vs = { ...state.viewState } as StorytellingTopicViewStateRecord;
            vs.PSTViewStateMapZoomToCoverage = !vs.PSTViewStateMapZoomToCoverage;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.toggleViewStateShowTeamLocations, (state): ViewStateState => {
        if (state.viewState && state.viewState.PSTViewStateType === ViewStateTypes.TOPIC) {
            const vs = { ...state.viewState } as StorytellingTopicViewStateRecord;
            vs.PSTViewStateMapShowTeamLocations = !vs.PSTViewStateMapShowTeamLocations;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.toggleViewStateLockMapLocation, (state): ViewStateState => {
        if (state.viewState && state.viewState.PSTViewStateType === ViewStateTypes.TOPIC) {
            const vs = { ...state.viewState } as StorytellingTopicViewStateRecord;
            vs.PSTViewStateMapLockMapLocation = !vs.PSTViewStateMapLockMapLocation;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateGroupBySortOrder, (state): ViewStateState => {
        if (state.viewState) {
            const vs = { ...state.viewState } as StorytellingSingleViewStateRecord;
            vs.PSTViewStateGroupSortOrder = vs.PSTViewStateGroupSortOrder === ViewStateSortOders.ASC ? ViewStateSortOders.DESC : ViewStateSortOders.ASC;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateSortBySortOrder, (state): ViewStateState => {
        if (state.viewState) {
            const vs = { ...state.viewState } as StorytellingSingleViewStateRecord;
            vs.PSTViewStateSortOrder = vs.PSTViewStateSortOrder === ViewStateSortOders.ASC ? ViewStateSortOders.DESC : ViewStateSortOders.ASC;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),

    on(ViewStateActions.updateViewStateSortFieldSortDir, (state, action): ViewStateState => {
        if (state.viewState) {
            const vs = { ...state.viewState } as StorytellingSingleViewStateRecord;
            vs.PSTViewStateSorting = { ...vs.PSTViewStateSorting, Value: action.sortBy };
            vs.PSTViewStateSortOrder = action.sortDirection as ViewStateSortOders;
            return handleUpdateViewState(state, vs);
        }
    }),

    on(ViewStateActions.updateViewStateGroupFieldGroupSortDir, (state, action): ViewStateState => {
        if (state.viewState) {
            const vs = { ...state.viewState } as StorytellingSingleViewStateRecord;
            vs.PSTViewStateGrouping = { ...vs.PSTViewStateGrouping, Value: action.groupBy };
            vs.PSTViewStateGroupSortOrder = action.sortDirection as ViewStateSortOders;
            return handleUpdateViewState(state, vs);
        }
    }),

    on(ViewStateActions.updateViewStateRevisionNumber, (state, action): ViewStateState => {
        if (state.viewState && state.viewState.PSTViewStateType === ViewStateTypes.PUBLICATION) {
            const vs = { ...state.viewState } as StorytellingPublicationViewStateRecord;
            vs.publicationRevisionNumber = action.revisionNumber;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateMaxRevisionNumber, (state, action): ViewStateState => {
        if (state.viewState && state.viewState.PSTViewStateType === ViewStateTypes.PUBLICATION) {
            const vs = { ...state.viewState } as StorytellingPublicationViewStateRecord;
            vs.maxPublicationRevisionNumber = (action.vs as StorytellingPublicationViewStateRecord).maxPublicationRevisionNumber;
            vs.publicationRevisionNumber = (action.vs as StorytellingPublicationViewStateRecord).publicationRevisionNumber;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateViewStateGuid, (state, action): ViewStateState => {
        if (state.viewState && state.viewState.PSTViewStateType === ViewStateTypes.PUBLICATION) {
            const vs = { ...state.viewState } as StorytellingPublicationViewStateRecord;
            vs.PSTViewStateGUID = action.guid;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateGridOrder, (state, action): ViewStateState => {
        if (state.viewState) {
            const vs = { ...state.viewState } as StorytellingSingleViewStateRecord;
            vs.PSTViewStateGridOrder = [...action.columns];
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.patchLocalViewState, (state, action): ViewStateState => {
        return handleUpdateViewState(state, action.vs);
    }),
    on(ViewStateActions.toggleFilterState, (state): ViewStateState => {
        if (state.viewState) {
            const vs = { ...state.viewState } as StorytellingViewStateRecord;
            vs.PSTViewStateFilterState = !vs.PSTViewStateFilterState;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.toggleFilterElevation, (state): ViewStateState => {
        const elevation: ViewStateFilterElevations = state.filterElevation === 'over' ? 'side' : 'over';
        if (state.viewState) {
            const vs = { ...state.viewState } as StorytellingViewStateRecord;
            vs.PSTViewStateFilterElevation = elevation;
            return handleUpdateViewState({ ...state, filterElevation: elevation }, vs);
        } else {
            return { ...state, filterElevation: elevation };
        }
    }),
    on(ViewStateActions.togglePlanningViewsFilterState, (state): ViewStateState => {
        return { ...state, planningViewsFilterState: state.planningViewsFilterState === ViewStateFilterStates.Expanded ? ViewStateFilterStates.Collapsed : ViewStateFilterStates.Expanded };
    }),
    on(ViewStateActions.toggleSortOder, (state): ViewStateState => {
        if (isSingleViewStateRecord(state.viewState)) {
            const vs = { ...state.viewState } as StorytellingSingleViewStateRecord;
            vs.PSTViewStateSortOrder = vs.PSTViewStateSortOrder === ViewStateSortOders.ASC ? ViewStateSortOders.DESC : ViewStateSortOders.ASC;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateCurrentMapCenter, (state, action): ViewStateState => {
        if (state.viewState && state.viewState.PSTViewStateType === ViewStateTypes.TOPIC) {
            const vs = { ...state.viewState } as StorytellingTopicViewStateRecord;
            vs.PSTViewStateCurrentMapCenter = action.center;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.updateViewStateCurrentMapZoom, (state, action): ViewStateState => {
        if (state.viewState && state.viewState.PSTViewStateType === ViewStateTypes.TOPIC) {
            const vs = { ...state.viewState } as StorytellingTopicViewStateRecord;
            vs.PSTViewStateMapCurrentZoom = action.zoom;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateActions.toggleGroupSortOder, (state): ViewStateState => {
        if (state.viewState && isSingleViewStateRecord(state.viewState)) {
            const vs = { ...state.viewState } as StorytellingSingleViewStateRecord;
            vs.PSTViewStateGroupSortOrder = vs.PSTViewStateGroupSortOrder === ViewStateSortOders.ASC ? ViewStateSortOders.DESC : ViewStateSortOders.ASC;
            return handleUpdateViewState(state, vs);
        }
        return state;
    }),
    on(ViewStateApiActions.applyTeamCustomCardFieldsSuccess, ViewStateActions.updateViewStateCustomCardFields, (state, action): ViewStateState => {
        if (state.viewState) {
            if (isSingleViewStateRecord(state.viewState) || state.viewState.PSTViewStateType === ViewStateTypes.WASTEBINSEARCH) {
                const vs = { ...state.viewState } as StorytellingSingleViewStateRecord;
                vs.PSTViewStateTopicCardCustomItems = Object.assign([], action.fields);
                vs.isViewStateCustomCardItemsBlank =
                    (vs as StorytellingSingleViewStateRecord).PSTViewStateTopicCardCustomItems && (vs as StorytellingSingleViewStateRecord).PSTViewStateTopicCardCustomItems.length > 0 ? false : true;
                return handleUpdateViewState(state, vs);
            }
        }
        return state;
    }),
    on(ViewStateActions.updateTemplateRefs, (state, action): ViewStateState => {
        return { ...state, mainCta: action.mainCta, secondaryCta: action.secondaryCta, viewOptions: action.viewOptions, rightSlot: action.rightSlot };
    }),
    on(NavigationApiActions.updateNavigationItemSuccess, (state, action): ViewStateState => {
        // TODO: we could add the default here????
        if (action.vs?.PSTViewStateType === ViewStateTypes.PUBLICATION) {
            const vs = { ...action.vs } as StorytellingPublicationViewStateRecord;
            // add publication time
            const publicationTimeFilter = vs.PSTViewStateFilter.Filters.find(f => f.ID === PSTFieldNames.PSTPublicationTime);
            if (publicationTimeFilter?.Value && PUBLICATION_LIST_TIME_DAYS_MAP[publicationTimeFilter.Value] != null) {
                let dt = DateTime.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
                dt = dt.plus({ days: PUBLICATION_LIST_TIME_DAYS_MAP[publicationTimeFilter.Value] });
                vs.publicationTime = dt;
            }
            return handleUpdateViewState(state, action.vs);
        }
        return state;
    }),
    on(ViewStateApiActions.loadViewStateRecords, (state): ViewStateState => {
        return {
            ...state,
            viewStatesLoading: true,
            viewStates: [],
        };
    }),
    on(ViewStateApiActions.loadViewStateRecordsSuccess, (state, action): ViewStateState => {
        return {
            ...state,
            viewStates: action.viewstates,
        };
    }),
    on(ViewStateApiActions.loadViewStateRecordsSuccess, ViewStateApiActions.loadViewStateRecordsError, (state): ViewStateState => {
        return {
            ...state,
            viewStatesLoading: false,
        };
    })
);
